// Rules shown on the ready-made pack pages (/printable/<type>/<tier>) for the
// printables that have their own page component. The data-driven ones reuse
// `rules` from src/data/printables.js.
export const SET_RULES = {
  nonograms: [
    ['Read the clues', 'The numbers beside a row (or above a column) are the lengths of its shaded blocks, in order, with at least one empty square between blocks.'],
    ['Start with big blocks', 'A block longer than half the line always covers its middle squares, wherever it slides.'],
    ['Mark the empties', 'Put a dot or an X in squares that must stay empty. They cut lines into smaller pieces.'],
    ['Cross over', 'Every square you settle in a row is new information for its column, and the other way round.'],
  ],
  'tent-logic': [
    ['One tent per tree', 'Every tree has exactly one tent beside it, up, down, left or right, and every tent belongs to one tree.'],
    ['Tents never touch', 'No two tents are next to each other, not even diagonally.'],
    ['Row and column counts', 'The numbers outside the grid say how many tents are in that row or column.'],
    ['Zeros first', 'A 0 means the whole line is grass, which often settles the trees next to it.'],
  ],
  'binary-sudoku-plus': [
    ['Zeros and ones', 'Fill every square with a 0 or a 1.'],
    ['No three in a row', 'Never put three 0s or three 1s next to each other in a row or column.'],
    ['Balance', 'Every row and column holds the same number of 0s and 1s, and no two rows or two columns are the same.'],
    ['Signs', 'An = between two squares means they match; an x means they differ.'],
  ],
  crowns: [
    ['One per line', 'Each row and each column holds exactly one crown.'],
    ['One per region', 'Each colour region holds exactly one crown.'],
    ['No touching', 'Crowns never touch, not even diagonally.'],
    ['Cross off', 'Mark every square that cannot hold a crown. The last open square in a row, column or region is the crown.'],
  ],
  sudoku: [
    ['Rows', 'Each row holds the numbers 1 to 9 exactly once.'],
    ['Columns', 'Each column holds the numbers 1 to 9 exactly once.'],
    ['Boxes', 'Each 3x3 box holds the numbers 1 to 9 exactly once.'],
    ['Pencil marks', 'Write small candidates in the corners of a square, and rub them out as the grid fills.'],
  ],
  calcudoku: [
    ['Latin square', 'Each number from 1 to N appears exactly once in every row and column.'],
    ['Cage maths', 'The numbers in a cage combine to the target with the operation shown (+, -, x or ÷).'],
    ['Single squares', 'A one-square cage simply gives you its number.'],
    ['List the options', 'Write down the number combinations that fit a cage, then let the rows and columns rule them out.'],
  ],
  aquarium: [
    ['Edge counts', 'The numbers outside the grid say how many squares in that row or column are full of water.'],
    ['Water is level', 'Within one tank, water fills from the bottom up and stays level across the whole tank.'],
    ['Full or empty rows', 'Each row of a tank is either completely full or completely empty.'],
    ['Work from the bottom', 'If a square in a tank is full, every square of that tank below it is full too.'],
  ],
};

// French pages (/fr/...). Only where Search Console shows French printable searches.
export const SET_RULES_FR = {
  nonograms: [
    ['Les indices', 'Les nombres au bout d\'une ligne (ou au-dessus d\'une colonne) donnent la longueur des blocs de cases noires, dans l\'ordre, séparés par au moins une case blanche.'],
    ['Les grands blocs d\'abord', 'Un bloc plus long que la moitié de la ligne couvre toujours les cases du milieu.'],
    ['Marquez les cases vides', 'Mettez un point ou une croix dans les cases qui restent blanches.'],
    ['Croisez', 'Chaque case trouvée dans une ligne aide à résoudre sa colonne, et inversement.'],
  ],
  'binary-sudoku-plus': [
    ['Des 0 et des 1', 'Chaque case contient un 0 ou un 1.'],
    ['Jamais trois de suite', 'Pas plus de deux 0 ou deux 1 côte à côte dans une ligne ou une colonne.'],
    ['Équilibre', 'Chaque ligne et chaque colonne contient autant de 0 que de 1, et deux lignes (ou deux colonnes) ne sont jamais identiques.'],
    ['Les signes', 'Un = entre deux cases indique qu\'elles sont égales, un x qu\'elles sont différentes.'],
  ],
};

export const SET_LABELS = {
  en: {
    eyebrow: 'Free printable · Answer key included',
    print: 'Print / Save as PDF',
    pdf: 'Download PDF',
    answers: 'Print the answer key',
    puzzle: 'Puzzle',
    answersTitle: 'Answers',
    rules: 'How to solve',
    packs: 'More packs',
    otherSizes: 'Other sizes',
    random: 'Make a random sheet',
    play: 'Play online',
    weekly: 'This week\'s mixed puzzle pack',
    allPrintables: 'All printable puzzles',
    classroom: 'Free to print and copy for home, classroom and library use. Every puzzle has one answer you can reach by logic alone.',
  },
  fr: {
    eyebrow: 'Gratuit à imprimer · Solutions incluses',
    print: 'Imprimer / Enregistrer en PDF',
    pdf: 'Télécharger le PDF',
    answers: 'Imprimer les solutions',
    puzzle: 'Grille',
    answersTitle: 'Solutions',
    rules: 'Les règles',
    packs: 'En anglais',
    otherSizes: 'Autres tailles',
    random: 'Plus de grilles (en anglais)',
    play: 'Jouer en ligne (en anglais)',
    weekly: 'Le pack de la semaine (en anglais)',
    allPrintables: 'Tous les puzzles à imprimer (en anglais)',
    classroom: 'Libre d\'impression et de photocopie pour la maison, la classe et la bibliothèque. Chaque grille a une seule solution, que l\'on trouve par la logique.',
  },
};

// Ready-made easy packs to suggest to teachers (/embed, /teachers-and-libraries).
export const CLASS_PACKS = [
  { path: '/printable/nonograms/easy-5x5', name: 'Nonograms, 5x5', note: 'shade squares to match the number clues; a great first logic puzzle' },
  { path: '/printable/crowns/easy-5x5', name: 'Crowns, 5x5', note: 'one crown per row, column and colour region' },
  { path: '/printable/tent-logic/easy-6x6', name: 'Tent Logic, 6x6', note: 'a tent beside every tree, with no two tents touching' },
  { path: '/printable/calcudoku/easy-4x4', name: 'Calcudoku, 4x4', note: 'arithmetic practice hidden inside a logic puzzle' },
  { path: '/printable/logic-grid-puzzles/easy', name: 'Logic grid puzzles, 3 categories', note: 'short riddles that teach reading clues carefully' },
  { path: '/printable/skyscrapers/easy-4x4', name: 'Skyscrapers, 4x4', note: 'count the buildings you can see from each side' },
];
